import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { videoService } from '@/services/videoService';
import { useNotificationsStore } from '@/store/notificationsStore';
import { useCustomAuth } from '@/contexts/CustomAuthContext';

const STORAGE_KEY = 'hostvsl_video_statuses';

// Statuses que indicam que o vídeo ainda não está pronto
const NOT_READY_STATUSES = new Set(['pending', 'processing', 'partial', 'queued', 'uploading']);

type VideoStatusMap = Record<string, string>;

function loadStoredStatuses(): VideoStatusMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveStatuses(statuses: VideoStatusMap) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(statuses));
  } catch {}
}

export function useVideoStatusWatcher() {
  useLanguage();
  const { isAuthenticated } = useCustomAuth();
  const addNotification = useNotificationsStore((s) => s.addNotification);
  const previousStatusesRef = useRef<VideoStatusMap>(loadStoredStatuses());
  const isFirstFetchRef = useRef(true);

  const { data } = useQuery({
    queryKey: ['videos', 'list', 'status-watcher'],
    queryFn: () =>
      videoService.getVideosList({
        page: 1,
        limit: 50,
        sortBy: 'createdAt',
        sortOrder: 'DESC',
      }),
    enabled: isAuthenticated,
    // Só faz poll quando há vídeos em andamento; caso contrário para.
    // 30s (não 3s): o processamento leva minutos, então essa granularidade é de sobra
    // pra notificar "vídeo pronto" — e essa rota (dashboard/list) é PESADA (agrega
    // plays/views em 2.7M de métricas), então não vale martelar a cada 3s só pra ler status.
    refetchInterval: (query) => {
      const videos = (query.state.data as any)?.videos ?? [];
      const hasPending = videos.some((v: any) => NOT_READY_STATUSES.has(v.status));
      return hasPending ? 30_000 : false;
    },
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (!data?.videos) return;

    // Na primeira busca apenas armazena os status atuais sem notificar
    if (isFirstFetchRef.current) {
      const initial: VideoStatusMap = {};
      for (const video of data.videos) {
        initial[video.id] = video.status;
      }
      previousStatusesRef.current = { ...previousStatusesRef.current, ...initial };
      saveStatuses(previousStatusesRef.current);
      isFirstFetchRef.current = false;
      return;
    }

    const previous = previousStatusesRef.current;
    const updated: VideoStatusMap = {};

    for (const video of data.videos) {
      const prevStatus = previous[video.id];
      const newStatus = video.status;
      updated[video.id] = newStatus;

      if (!prevStatus || prevStatus === newStatus) continue;

      // → partial: pronto para personalizar (pending/processing → partial)
      if (newStatus === 'partial' && NOT_READY_STATUSES.has(prevStatus)) {
        addNotification({
          type: 'partial',
          videoId: video.id,
          videoTitle: video.title,
          message: translateInterfaceText("\"{value0}\" já pode ser personalizado! O MP4 ainda está sendo gerado.", { value0: (video.title) }),
        });
      }

      // → active: completamente pronto (qualquer status → active)
      if (newStatus === 'active' && prevStatus !== 'active') {
        addNotification({
          type: 'active',
          videoId: video.id,
          videoTitle: video.title,
          message: translateInterfaceText("\"{value0}\" está completamente pronto e disponível para visualização!", { value0: (video.title) }),
        });
      }
    }

    previousStatusesRef.current = { ...previous, ...updated };
    saveStatuses(previousStatusesRef.current);
  }, [data, addNotification]);
}
